import { Chart, ChartConfiguration } from 'chart.js';

export type Color = 'orange' | 'red' | 'grey';

export type GaugeRenderOptions = {
  circleBorder?: boolean;
  arrowOffset?: number;
  useNewArrow?: boolean;
  animation?: {
    duration?: number;
    delay?: number;
    arrowEndOffset?: number;
  };
};

function index(perc: number) {
  return perc < 33.3 ? 0 : perc < 66.6 ? 1 : 2;
}

export function transformValue(value: number) {
  return value < 33.3 ? 33 : value < 66.6 ? 66 : 100;
}

const ANGLES = [-2.6, -1.6, -0.5];
const NEW_ANGLES = [-1.2, 0, 1.2];

// Importing a svg as an external svg would be too complicated,
// since we only used it here, we can just used an embedded version of it.
const ARROW_IMAGE =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzAiIHZpZXdCb3g9IjAgMCAzMiAzMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMi44MTcxNyAxMC45OTc0QzAuNTA2MzI3IDYuOTk5NDEgMy4zOTY2MiAxLjk5OTMgOC4wMTg3NiAxLjk5OTE0TDIzLjM4MjkgMS45OTkzNEMyOC4wMDMyIDEuOTk5MzQgMzAuODg4OSA2Ljk5NjExIDI4LjU3NzcgMTAuOTk0M0wyMC44OTU4IDI0LjI4MzNDMTguNTg0NSAyOC4yODEyIDEyLjgwOTUgMjguMjgyOSAxMC40OTk0IDI0LjI4NjdMMi44MTcxNyAxMC45OTc0WiIgZmlsbD0iIzQ4NTM4QyIgc3Ryb2tlPSIjRjdGOEYzIiBzdHJva2Utd2lkdGg9IjQiLz48L3N2Zz4=';

export function getConfig(
  value: number,
  color: string,
  options?: GaugeRenderOptions,
): ChartConfiguration<'doughnut'> {
  const data: ChartConfiguration<'doughnut'>['data'] = {
    datasets: [
      {
        data: [value, 100 - value],
        backgroundColor(ctx) {
          if (ctx.type !== 'data') {
            return;
          }
          if (ctx.dataIndex === 1) {
            return 'rgb(234, 234, 234)';
          }
          const chartArea = ctx.chart.chartArea;

          if (!chartArea) return color;

          const gradient = ctx.chart.ctx.createLinearGradient(
            chartArea.left,
            chartArea.top,
            chartArea.right,
            chartArea.bottom,
          );
          gradient.addColorStop(0, '#FFFFFF');
          gradient.addColorStop(value / 100, color);

          return gradient;
        },
      },
    ],
  };

  const progressRef = { value: 0 };

  const duration = options?.animation?.duration ?? 300;
  const delay = options?.animation?.delay ?? 0;
  const arrowEndOffset = options?.animation?.arrowEndOffset ?? -300;

  const shouldShowBorder = options?.circleBorder !== false;
  const config: ChartConfiguration<'doughnut'> = {
    type: 'doughnut',
    data,
    options: {
      animation: {
        loop: false,
        easing: 'easeOutQuad',
        duration: duration,
        delay: delay,
        animateRotate: true,
        onProgress: options?.useNewArrow
          ? animation => {
              if (animation.initial && duration > 0) {
                const adjustedDuration = duration - delay + arrowEndOffset;
                const elapsed = animation.currentStep - delay;
                progressRef.value = elapsed / adjustedDuration;
              } else {
                progressRef.value = 1;
              }
            }
          : undefined,
      },
      borderColor: shouldShowBorder ? '#FFFFFF' : 'transparent',
      aspectRatio: 2,
      circumference: 180,
      rotation: -90,
      cutout: '90%',
      responsive: true,
      interaction: {
        mode: undefined,
      },
      plugins: {
        tooltip: {
          enabled: false,
        },
      },
    },
    plugins: [
      options?.useNewArrow
        ? {
            id: 'gaugeChartPlugin',
            afterDraw: chart =>
              drawNewArrow(chart, progressRef, value, options),
          }
        : {
            id: 'gaugeChartPlugin',
            afterDraw: chart => drawOldArrow(chart, value, options),
          },
    ],
  };

  return config;
}

function drawNewArrow(
  chart: Chart<'doughnut'>,
  progressRef: { value: number },
  value: number,
  options?: GaugeRenderOptions,
) {
  const progress = progressRef.value;
  if (progress <= 0) {
    return; // Don't draw the arrow if the animation hasn't started
  }

  const START_ANGLE = -Math.PI / 2;
  const targetAngle = NEW_ANGLES[index(value)];
  // Move the arrow alongside the animation with a linear interpolation
  const angle = START_ANGLE + (targetAngle - START_ANGLE) * progress;

  const ctx = chart.ctx;
  const cw = chart.canvas.offsetWidth;
  const ch = chart.canvas.offsetHeight;
  const cwHalf = cw / 2;

  const arrowWidth = 24;
  const arrowHeight = 24;
  const radius = cwHalf + (options?.arrowOffset || 0);

  const pivotX = cwHalf;
  const pivotY = ch;

  const image = new Image();
  image.src = ARROW_IMAGE;

  ctx.translate(pivotX, pivotY);
  ctx.rotate(angle);
  ctx.translate(-pivotX, -pivotY);

  ctx.drawImage(
    image,
    pivotX - arrowWidth / 2,
    pivotY - radius + arrowHeight / 2,
    arrowWidth,
    arrowHeight,
  );
  ctx.resetTransform();
}

function drawOldArrow(
  chart: Chart<'doughnut'>,
  value: number,
  options?: GaugeRenderOptions,
) {
  const angle = ANGLES[index(value)];

  const ctx = chart.ctx;
  const cw = chart.canvas.offsetWidth;
  const ch = chart.canvas.offsetHeight;
  const cx = cw / 2;
  const cy = ch - 6;

  // Triangle properties
  const radius = ch - 25 + (options?.arrowOffset || 0); // distance from center to triangle tip
  const baseWidth = 15; // width of triangle base
  const height = 15; // length from base to tip

  // Tip of triangle (points to center)
  const tipX = cx + radius * Math.cos(angle);
  const tipY = cy + radius * Math.sin(angle);

  // Base center (farther from center)
  const baseCenterX = cx + (radius + height) * Math.cos(angle);
  const baseCenterY = cy + (radius + height) * Math.sin(angle);

  // Base corners
  const perpAngle = angle + Math.PI / 2;
  const baseLeftX = baseCenterX + (baseWidth / 2) * Math.cos(perpAngle);
  const baseLeftY = baseCenterY + (baseWidth / 2) * Math.sin(perpAngle);
  const baseRightX = baseCenterX - (baseWidth / 2) * Math.cos(perpAngle);
  const baseRightY = baseCenterY - (baseWidth / 2) * Math.sin(perpAngle);

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(tipX, tipY);
  ctx.lineTo(baseLeftX, baseLeftY);
  ctx.lineTo(baseRightX, baseRightY);
  ctx.closePath();
  ctx.lineJoin = 'round';
  ctx.strokeStyle = '#FFFFFF';
  ctx.fillStyle = '#ABB8FF';
  ctx.lineWidth = 4;
  ctx.stroke();
  ctx.fill();
  ctx.restore();
}
